import { getRaisedCardColors } from 'components/theme/raisedCard';
import { getSegmentedControlColors } from 'scenes/ProjectBank/components/Toolbar/BankSegmentedControl';

/**
 * Corner of a card in the redesign (a project row, a dashboard tile, the
 * project's header card, a panel): the project bank's file cards'. Controls
 * (buttons, inputs, segmented groups) take ROUNDED_CONTROL_RADIUS from
 * components/theme/controlRadius instead, as they do in the bank.
 */
export const REDESIGN_CARD_RADIUS = 8;

/**
 * Whether a classic surface token already draws a border of its own (the
 * light and the dark theme give some of them one, the classic theme none).
 * The redesign then recolours that border rather than drawing a second edge,
 * so a box keeps its size in every theme.
 * @param {Object} [classicStyle] the classic token, e.g. theme.component.dashboardPaper
 * @returns {boolean}
 */
export const hasClassicBorder = (classicStyle) =>
  Boolean(
    classicStyle && classicStyle.border && classicStyle.border !== 'none'
  );

/**
 * The edge of a redesigned card drawn on the box itself: the classic border
 * recoloured where the theme has one, else a hairline inside the box. For a
 * box whose children paint over its inside (hover fills), use
 * getRedesignCardEdgeLayer instead.
 * @param {Object} [classicStyle] the classic token the box is styled with
 * @param {string} color edge colour
 * @returns {Object} JSS style
 */
export const getRedesignCardEdge = (classicStyle, color) =>
  hasClassicBorder(classicStyle)
    ? { borderColor: color }
    : { boxShadow: `inset 0 0 0 1px ${color}` };

/**
 * The same edge for a box whose children paint over its inside (the cells'
 * hover fills of a project row): the classic border recoloured where the
 * theme has one — a border is painted outside the children anyway — else the
 * hairline on a `::after` layer over the whole box, the last thing painted
 * in it, following its corner and costing it no size. The box needs
 * `position: relative`.
 * @param {Object} [classicStyle] the classic token the box is styled with
 * @param {string} color edge colour
 * @returns {Object} JSS style
 */
export const getRedesignCardEdgeLayer = (classicStyle, color) =>
  hasClassicBorder(classicStyle)
    ? { borderColor: color }
    : {
        '&::after': {
          content: '""',
          position: 'absolute',
          top: 0,
          right: 0,
          bottom: 0,
          left: 0,
          borderRadius: 'inherit',
          pointerEvents: 'none',
          boxShadow: `inset 0 0 0 1px ${color}`,
        },
      };

/**
 * A raised card of the redesign: the raised fill, its hairline drawn on the
 * card (the classic border recoloured where the theme has one, else an inset
 * ring that costs the box no size) and the drop shadow behind it.
 * @param {Object} [classicStyle] the classic token the box is styled with
 * @param {Object} colors getProjectPageRedesignColors(theme)
 * @returns {Object} JSS style
 */
export const getRedesignRaisedCard = (classicStyle, colors) =>
  hasClassicBorder(classicStyle)
    ? {
        backgroundColor: colors.raisedCard,
        borderColor: colors.raisedEdge,
        boxShadow: colors.raisedShadow,
      }
    : {
        backgroundColor: colors.raisedCard,
        boxShadow: `inset 0 0 0 1px ${colors.raisedEdge}, ${colors.raisedShadow}`,
      };

// The colour of a border token written as a shorthand (`1px solid <colour>`).
const colourOfBorder = (border) => {
  const match = /^\s*\S+\s+\S+\s+(.+?)\s*$/.exec(border || '');
  return match ? match[1] : null;
};

/**
 * The redesigned projects page's colours, by role. Every one of them is a
 * project bank token of the current theme (theme.component.projectBank*,
 * built by projectBankComponentTokens in the three theme files) or the bank's
 * segmented-control fills, so the page and the bank read as one design in the
 * classic, the dark and the light theme alike, and a change to a bank token
 * carries over.
 * @param {Object} theme MUI theme
 * @returns {{
 *   canvas: string, pane: string, border: string, edge: string,
 *   card: string, cardBorder: string,
 *   raisedCard: string, raisedEdge: string, raisedShadow: string, cardHover: string, cardSelected: string,
 *   text: string, muted: string, well: string,
 *   chip: string, chipText: string,
 *   accent: string, accentText: string,
 *   outline: string, hoverFill: string, selectedFill: string,
 * }}
 */
export const getProjectPageRedesignColors = (theme) => {
  const {
    projectBankSurface,
    projectBankSurfaceRaised,
    projectBankBorder,
    projectBankCard,
    projectBankCardHover,
    projectBankCardSelected,
    projectBankMuted,
    projectBankMedia,
    projectBankChip,
    projectBankAccent,
  } = theme.component;
  const { outline, hoverFill, selectedFill } = getSegmentedControlColors(theme);
  const { borderMust } = theme.component;
  const raised = getRaisedCardColors(theme);

  return {
    // The dashboard's ground (the bank's content pane).
    canvas: projectBankSurface.backgroundColor,
    // The project list's column (the bank's tree pane).
    pane: projectBankSurfaceRaised.backgroundColor,
    // Edges between panes and inside cards.
    border: projectBankBorder.borderColor,
    // The outline of the dashboard's cards (the overview, the feature tiles):
    // the line between the project bank's folder pane and its content,
    // stronger than the cards' own hairline.
    edge: colourOfBorder(borderMust) || projectBankBorder.borderColor,
    card: projectBankCard.backgroundColor,
    cardBorder: projectBankCard.borderColor,
    // A card that stands off the ground (the overview, the dashboard tiles,
    // the project rows): a lighter fill, a quiet hairline on the card itself
    // and a drop shadow behind it, in place of the bright edge.
    raisedCard: raised.fill,
    raisedEdge: raised.edge,
    raisedShadow: raised.shadow,
    cardHover: projectBankCardHover.backgroundColor,
    cardSelected: projectBankCardSelected.backgroundColor,
    text: projectBankCard.color,
    muted: projectBankMuted.color,
    // A well inside a card (the worker list, the description): the bank's
    // thumbnail tint.
    well: projectBankMedia.backgroundColor,
    // A neutral tag on a card.
    chip: projectBankChip.backgroundColor,
    chipText: projectBankChip.color,
    accent: projectBankAccent.backgroundColor,
    accentText: projectBankAccent.color,
    // Outlined controls and segmented groups.
    outline,
    hoverFill,
    selectedFill,
  };
};
